import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  getMonthlyRecurringCharges,
  calculateRentWithAmenities,
} from '../../../utils/lease-helpers';
import { formatCurrency } from '../../../utils';
import messages from './messages';

const MonthlyRecurringCharges = ({ unit, intl }: Object) => {
  const featureFlags = useFlags();

  // Quoting rent with amenities included
  const quotingRentAmount = useMemo(
    () => calculateRentWithAmenities(unit, null, featureFlags),
    [unit, featureFlags],
  );

  // Get recurring fees for display
  const recurringFees = useMemo(() => getMonthlyRecurringCharges(unit), [unit]);

  // Calculate total: quoting rent with amenities + recurring fees
  const monthlyRentAmount = useMemo(() => {
    const totalRecurringFees = recurringFees.reduce(
      (total, fee) => total + Number(fee.value || 0),
      0,
    );
    return Number(quotingRentAmount || 0) + totalRecurringFees;
  }, [quotingRentAmount, recurringFees]);

  return (
    <div className="panel unit-charges panel-has-table">
      <div className="panel-head">
        <h2>
          <FormattedMessage {...messages.title} />
        </h2>
      </div>
      <div className="unit-details">
        <table className="table table-striped">
          <tbody>
            <tr>
              <td style={{ width: '70%' }}>
                <FormattedMessage {...messages.unitQuotingRentAmount} />
              </td>
              <td>{formatCurrency(intl, quotingRentAmount || 0)}</td>
            </tr>
            {recurringFees
              .sort((a, b) => b.value - a.value)
              .map((fee, key) => (
                <tr key={key}>
                  <td>{fee.label}</td>
                  <td>
                    {fee.descriptiveValue
                      ? fee.descriptiveValue
                      : formatCurrency(intl, fee.value)}
                  </td>
                </tr>
              ))}
            <tr className="table-section table-active">
              <td>
                <FormattedMessage {...messages.total} />
              </td>
              <td>{formatCurrency(intl, monthlyRentAmount)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MonthlyRecurringCharges;
